import {
  isOverseas,
  isValidCitizenId,
  isValidFPR,
  isValidFWP,
  isValidHKId,
  isValidHKMACP,
  isValidMOPId,
  isValidTWId,
  isValidTWTPP,
} from '@moka-fe/ats.utils';
import { get, isString } from 'lodash';
import request from 'superagent';

import { LOCALES, getI18n } from './i18n';
import {
  CERTIFICATE_TYPE,
  CURRENT_COUNTRY_CALLING_CODE,
  DEFAULT_NETWORK_ERROR_MESSAGE,
  SALARY_PAYMENT_METHOD,
  SALARY_PAYMENT_METHOD_NAMES,
  SALARY_UNIT,
  SALARY_UNIT_NAMES,
} from '@/constants/common';
import { cityNamesOnId } from '@/constants/location';

const i18n = getI18n();
/**
 * 将 `+{callingCode} {phone}` 字符串电话，转为 [callingCode, phone] 格式
 * @param phoneVal {String} - +86 Phone
 * @param showChineseMainlandCallingCode - {boolean: default true} 是否显示中国大陆国家调用代码，只有在 PhoneInput 组件中为 true
 * @returns {Array} - [callingCode, phone]
 */
export function parsePhone(phoneVal, showChineseMainlandCallingCode) {
  if (!phoneVal) {
    return [null, null];
  }

  let callingCode = null;
  let phone = null;

  const res = /^\+([\S]{0,4}) ?([\S|-]+)?/.exec(phoneVal);

  // 匹配到带区号的 号码
  // ps: 没有输入区号
  if (!res) {
    return [null, String(phoneVal)];
  }

  // 匹配到带区号的 号码
  // ps: 并且区号不是 86
  if (res && (res[1] !== CURRENT_COUNTRY_CALLING_CODE || showChineseMainlandCallingCode)) {
    callingCode = res[1];
  }

  // 匹配到 号码
  if (res && typeof res[2] !== 'undefined') {
    phone = res[2];
  }

  return [callingCode, phone];
}

/**
 * 将 电话号[callingCode, phone] 转为 `+{callingCode}.{phone}` 字符串格式
 * @param callingCode - {string}
 * @param phoneVal - {string}
 * @param showChineseMainlandCallingCode - {boolean: default false} 是否显示中国大陆国家调用代码，只有在 PhoneInput 组件中为 true
 * @returns {string}
 */
export function stringifyPhone([callingCode, phoneVal], showChineseMainlandCallingCode) {
  callingCode = callingCode === null ? CURRENT_COUNTRY_CALLING_CODE : callingCode;
  if (!showChineseMainlandCallingCode && callingCode === CURRENT_COUNTRY_CALLING_CODE) {
    callingCode = null;
  }
  return callingCode ? `+${callingCode} ${phoneVal}` : `${phoneVal}`;
}

/**
 * 验证电话号码
 * @param input {string}
 * @returns {boolean}
 */
export function phoneValidator(input) {
  // whitespaces will be trimmed.
  if (!input) {
    return false;
  }
  const [callingCode, phoneVal] = parsePhone(input); // parsePhone 会将 '86' 转为 null

  // 没有解析出电话号码
  if (!phoneVal) {
    return false;
  }

  // 没有区号，按照 大陆电话验证
  if (!callingCode) {
    return (
      phoneVal.match(/^\s*(\+86)?(1)(\d{10})\s*$/) ||
      phoneVal.match(/^\s*(\d{3}|\d{4})-(\d{7}|\d{8})\s*$/)
    );
  }

  return phoneVal.match(/^(\d{6,14}$)/);
}

/** 是否填写了手机号的区号 */
export function validateMobilePhoneCallingCode(input) {
  if (!input) {
    return false;
  }

  const [callingCode] = parsePhone(input, true);
  return !!callingCode;
}

/**
 * 完整的手机号校验，包括区号和手机号
 * 暂时只有海外校验区号必填
 * @param {string} value
 * @returns {{isValid: boolean, message: string}}
 */
export function mobilePhoneValidator(value) {
  // 海外校验区号是否填写
  if (isOverseas && !validateMobilePhoneCallingCode(value)) {
    return {
      isValid: false,
      message: i18n.t`请填写区号`,
    };
  }
  if (!phoneValidator(value)) {
    return {
      isValid: false,
      message: i18n.t`电话格式有误`,
    };
  }

  return {
    isValid: true,
    message: '',
  };
}

export function emailValidator(input) {
  // whitespaces will be trimmed.
  return (
    input && input.match(/^\s*[a-zA-Z0-9._%+-]*[a-zA-Z0-9_%+-]@([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}\s*$/)
  );
}

export function nameValidator(input) {
  return input && input.length <= 50;
}

// 短信验证码校验，必须是4-10位，数字或者大写字母
export function smsCodeValidator(input) {
  return input && isString(input) && input.match(/^(\d|[A-Z]){4,10}$/g);
}

/**
 * Use iframe to download
 *
 * @param {String} url - the download url
 * @param {Object|undefined} data - Optional. If data exists, it will
 *                           send data(each value of the object will be stringify by JSON)
 *                           to the download url with POST method.
 */
export function downloadByUrl(url, data = null) {
  const downloaderId = 'downloader';
  // hacky way to download file
  let downloader = document.getElementById(downloaderId);
  if (!downloader) {
    downloader = document.createElement('iframe');
    downloader.id = downloaderId;
    downloader.style.display = 'none';
    downloader.setAttribute('name', downloaderId);
    document.body.appendChild(downloader);
  }
  if (data) {
    const formId = 'downloadForm';
    let former = document.getElementById(formId);
    if (!former) {
      former = document.createElement('form');
      former.id = formId;
      former.setAttribute('method', 'POST');
      former.setAttribute('target', downloaderId);
      document.body.appendChild(former);
    }
    while (former.firstChild) {
      former.removeChild(former.firstChild);
    }
    Object.keys(data).forEach((field) => {
      const input = document.createElement('input');
      input.setAttribute('type', 'hidden');
      input.setAttribute('name', field);
      input.setAttribute('value', JSON.stringify(data[field]));
      former.appendChild(input);
    });
    former.setAttribute('action', url);
    former.submit();
  } else {
    downloader.src = url;
  }
}

/**
 * Check response status of `superagent`
 * Return a promise that:
 *   if response is success, resolves to response body.
 *   else rejects to error message.
 *
 * @param  {Object} err Error object of superagent
 * @param  {Object} res Response object of superagent
 * @param  {Boolean} messageOnly - indicate whether to reject
 *                                res.body or res.body.message
 * @return {Promise}
 */
export function checkResponse(err, res, messageOnly = true) {
  return new Promise((resolve, reject) => {
    if (!err && res.ok) {
      return resolve(res.body);
    }
    try {
      reject(messageOnly ? res.body.message : res.body);
    } catch (e) {
      reject(DEFAULT_NETWORK_ERROR_MESSAGE);
    }
  });
}

/**
 * 给superagent加上checkResponse方法，调用后直接返回checkResponse后的Promise
 */
request.Request.prototype.checkResponse = function () {
  const self = this;
  return new Promise((resolve, reject) => {
    self.end((err, res) => checkResponse(err, res).then(resolve).catch(reject));
  });
};

/**
 * generate local-storage key
 *
 * @param {String} key - original key string
 * @param {Object} query - query object
 * @return {String} decorated key string
 */
function calcLocalStorageKey(key, query = {}) {
  query.userId = query.userId || get(window, 'MOKA_USER.id');
  query.hireMode = query.hireMode || get(window, 'MOKA_DEFAULT.hireMode');

  const queryKeys = Object.keys(query);
  if (queryKeys.length <= 0) {
    return key;
  } else {
    queryKeys.sort();
    const search = queryKeys.reduce(
      (result, queryKey) => `${result}&${queryKey}=${query[queryKey]}`,
      ''
    );
    return key + '?' + search.slice(1);
  }
}

/**
 * Set local-storage item.
 *
 * @param {String} key - Key of the local-storage item.
 * @param {any} value - Value of the local-storage item.
 * @param {Object=} query - query object
 */
export function setLocalStorage(key, value, query) {
  try {
    key = calcLocalStorageKey(key, query);
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch (e) {
    // do nothing.
  }
}

/**
 * Get local-storage item value.
 * @param  {String} key - Key of the local-storage item.
 * @param  {?} defaultValue - If no local-storage item is found, return the default value.
 * @param  {Object} query - 仿照url的规则，可以放置一些参数
 * @return {?} local-storage item value.
 */
export function getLocalStorage(key, defaultValue, query) {
  try {
    key = calcLocalStorageKey(key, query);
    const value = window.localStorage.getItem(key);
    const result = JSON.parse(value);
    if (result === 0) {
      // 因为 0 || * 最后返回的都是*，做个特殊处理。
      return result;
    }
    return result || defaultValue;
  } catch (e) {
    return defaultValue;
  }
}

/*
 * 获取用户显示的名字
 * 有些情况下（历史遗留数据），用户的名字是空，甚至email也是空，
 * 所以需要降级显示，优先显示名字，其次是email，如果都没有则是空。
 *
 * @param  {String} options.name - 用户名字
 * @param  {String} options.email - 用户邮箱
 * @return {String} 显示的名字
 */
export function getReadableUserName(user) {
  const { name, email } = user || {};
  if (name) {
    return name;
  }
  if (email) {
    return email.split('@')[0];
  }
  return '';
}

/**
 * Get readable file size from bytes.
 * e.g. 10240 --> 10KB
 *
 * @param  {Number} size Bytes count.
 * @return {String}      Human readable file size.
 */
export function getReadableFileSize(size) {
  if (size < 0) {
    return 'Invalid file size';
  }
  if (size === null) {
    return '';
  }

  let i = -1;
  do {
    size /= 1024;
    i++;
  } while (size > 1024);

  return Math.max(size, 0.1).toFixed(1) + ('KMGTPEZYB'[i] || '') + 'B';
}

/**
 * Convert cityId to city object
 *
 * @param {String|Number} cityId - e.g. 110108
 *
 * @return {Object} an object may contains name of province, city and area
 *                 e.g. { province: '北京', area: '海淀区' } or { province: '河北', city: '石家庄市', area: '长安区' }
 */
function cityIdToObj(cityId) {
  if (!cityId) {
    return {};
  }
  const province = cityNamesOnId[`${String(cityId).slice(0, 2)}0000`];
  const city = cityNamesOnId[`${String(cityId).slice(0, 4)}00`];
  const area = cityNamesOnId[cityId];
  return {
    province,
    city: city !== province ? city : null,
    area: area !== city ? area : null,
  };
}

/**
 * Generate formatted location by country, cityId from locationObject
 *
 * @param {Object} locationObject - it contains country, cityId and address
 * @param {String} separator
 *
 * @return {String} formatted location - e.g. '北京 海淀区'
 */
export function formatLocationToShortStr({ country, cityId, address } = {}, separator = ' ') {
  if (country && country !== '中国') {
    return country;
  }
  const formattedArr = [];
  const { province, city, area } = cityIdToObj(cityId);
  if (province) {
    formattedArr.push(province);
  }
  if (city) {
    formattedArr.push(city);
  }
  if (area) {
    formattedArr.push(area);
  }

  // 公司有时候会不选省市， 在地址栏写“全国”， 下面是这种情况
  if (!formattedArr.length && address) {
    formattedArr.push(address);
  }
  return formattedArr.slice(0, 2).join(separator);
}

/**
 * Generate formatted locations by country, cityId from locationObjectList
 *
 * @param {Array} locations
 * @param {String} separator
 *
 * @return {String} formatted location - e.g. '北京 海淀区 等x个地点'
 */
export function formatLocationListToShortStr(locations, separator = ' ') {
  if (!locations || locations.length === 0) {
    return '';
  }
  const locationStr = formatLocationToShortStr(locations[0], separator);
  if (locations.length === 1) {
    return locationStr;
  }
  return i18n.t`${locationStr} 等${locations.length}个地点`;
}

export function isContainNumber(str) {
  const rex = /\d/;
  return rex.test(str);
}

export function isContainUpperCase(str) {
  const rex = /[A-Z]/;
  return rex.test(str);
}

export function isContainLowerCase(str) {
  const rex = /[a-z]/;
  return rex.test(str);
}

export function isContainSpecialChar(str) {
  const rex = /[!"#$%&'()*+,\-./:;<=>?@\[\\\]^_`{|}~]/;
  return rex.test(str);
}

export function isNullOrUndefined(value) {
  // eslint-disable-next-line no-undefined
  return value === null || value === undefined;
}

// eslint-disable-next-line @moka-fe/no-func-rest-arguments
export function isEmpty(...values) {
  return values.every((value) => value !== 0 && !value);
}

/**
 * 文件上传时，过滤掉不可用的文件类型
 */
export function filterDisabledFile(fileTypes, disabledType) {
  let filteredFileTypes = fileTypes;
  if (disabledType.includes('html/htm')) {
    filteredFileTypes = filteredFileTypes.replace(/\.html|text\/html/g, '');
  }
  if (disabledType.includes('rtf')) {
    filteredFileTypes = filteredFileTypes.replace(
      /application\/rtf|application\/x-rtf|text\/richtext|text\/rtf|\.rtf/g,
      ''
    );
  }
  return filteredFileTypes;
}

export function validateCertificate(value, type) {
  switch (type) {
    case CERTIFICATE_TYPE.CITIZEN_ID:
      return isValidCitizenId(value);
    case CERTIFICATE_TYPE.CITIZEN_ID_HK:
      return isValidHKId(value);
    case CERTIFICATE_TYPE.CITIZEN_ID_MO:
      return isValidMOPId(value);
    case CERTIFICATE_TYPE.CITIZEN_ID_TW:
      return isValidTWId(value);
    case CERTIFICATE_TYPE.FOREIGNER_RESIDENCE_PERMIT:
      return isValidFPR(value);
    case CERTIFICATE_TYPE.FOREIGNER_WORK_PERMIT:
      return isValidFWP(value);
    case CERTIFICATE_TYPE.MAINLAND_TRAVEL_PERMIT_TW:
      return isValidTWTPP(value);
    case CERTIFICATE_TYPE.MAINLAND_TRAVEL_PERMIT_HK:
      return isValidHKMACP(value);
    case CERTIFICATE_TYPE.OTHER:
      return true;
    default:
      return value && !!value.match(/^[0-9a-zA-Z]*$/);
  }
}

/**
 * 千分位格式化货币
 * 例如：1234567 -> 1,234,567
 *
 * @param {String} value
 * @returns {String}
 */
export const formatCurrency = (value) => {
  const currency = String(value).split('.');
  currency[0] = currency[0].replace(/(\d)(?=(\d{3})+$)/g, '$1,');
  return currency.join('.');
};

/**
 * 获取格式化后的薪资范围展示值
 *
 * @param {Object} { minSalary, maxSalary, payPeriod, salaryUnit, paymentMethod }
 * @returns {String}
 */
export const getSalaryRangeDisplay = ({
  minSalary,
  maxSalary,
  payPeriod,
  salaryUnit,
  paymentMethod,
}) => {
  const salaryRange = [];

  if (minSalary) {
    salaryRange.push(isOverseas ? formatCurrency(minSalary) : `${minSalary}`);
  }

  if (maxSalary) {
    salaryRange.push(isOverseas ? formatCurrency(maxSalary) : `${maxSalary}`);
  }

  if (minSalary && !maxSalary) {
    salaryRange[0] = i18n.t`${salaryRange[0]}以上`;
  }

  if (!minSalary && maxSalary) {
    salaryRange[0] = i18n.t`最高${salaryRange[0]}`;
  }

  let salaryRangeStr = salaryRange.join(' - ');

  if (!salaryRangeStr) {
    return '';
  }

  if (!!salaryUnit || salaryUnit === SALARY_UNIT.K_MONTH) {
    salaryRangeStr += ` ${SALARY_UNIT_NAMES[salaryUnit]}`;
  }

  if (payPeriod) {
    salaryRangeStr += ` x ${i18n.t`${payPeriod}个月`}`;
  }

  if (!!paymentMethod || paymentMethod === SALARY_PAYMENT_METHOD.DAY) {
    salaryRangeStr += ` (${SALARY_PAYMENT_METHOD_NAMES[paymentMethod]})`;
  }
  return salaryRangeStr;
};

/**
 * 从多选字段选中的值
 * 获取指定语言的本地化值，如果未找到则返回默认语言的值。
 */
export const parseMultiSelectValueDisplayLabel = (multipleSelectValue) => {
  if (!Array.isArray(multipleSelectValue)) {
    return '';
  }
  const selectLabels = multipleSelectValue.map((item) => {
    const localeValues = item?.localeValues;
    if (!localeValues) {
      return '';
    }
    let defaultLocaleValue;

    for (const localeValue of localeValues) {
      if (localeValue?.locale === i18n.language) {
        return localeValue.value;
      }
      if (localeValue?.locale === LOCALES.ZH_CN) {
        defaultLocaleValue = localeValue.value;
      }
    }

    return defaultLocaleValue || '';
  });

  return (selectLabels || []).join('、');
};
